import { useConfirmPopoverStore } from "@/shared/stores";
import {
  type AccessControlListItemResponseDto,
  type SubjectDto,
  SubjectDtoType,
} from "@/spz-api/model";
import { IconButton } from "@spz/ui/components/button";
import Loader from "@spz/ui/components/loader";
import { Typography } from "@spz/ui/components/typography";
import { useTranslation } from "react-i18next";
import { selectAccessControlViewModel } from "../view-models/select-access-control-view-model";

interface AccessControlsSheetContentProps {
  subjectId: SubjectDto["id"];
  subjectType: SubjectDtoType;
}

export const AccessControlListing = ({
  subjectId,
  subjectType,
}: AccessControlsSheetContentProps) => {
  const {
    directAccessControls,
    inheritedAccessControls,
    accessControlsPending,
  } = selectAccessControlViewModel({ subjectId, subjectType });

  return (
    <div className="flex flex-col h-full space-y-6">
      <div className="flex flex-col space-y-4">
        <Typography
          className="my-1 text-primary font-bold"
          variant={"h6"}
          trans={
            subjectType === SubjectDtoType.USER
              ? "direct_access_control"
              : "permissions"
          }
        />
        {accessControlsPending ? (
          <Loader size="size-6" />
        ) : directAccessControls.length === 0 ? (
          <Typography trans="no_permissions" />
        ) : (
          directAccessControls.map((item) => (
            <AccessControlListItem key={item.id} item={item} />
          ))
        )}
      </div>
      {subjectType === SubjectDtoType.USER && (
        <div className="flex flex-col space-y-4">
          <Typography
            className="my-1 text-primary font-bold"
            variant={"h6"}
            trans="inherited_access_control"
          />
          {accessControlsPending ? (
            <Loader size="size-6" />
          ) : inheritedAccessControls.length === 0 ? (
            <Typography trans="no_permissions" />
          ) : (
            inheritedAccessControls.map((item) => (
              <AccessControlListItem key={item.id} item={item} inherited />
            ))
          )}
        </div>
      )}
    </div>
  );
};

interface AccessControlListItemProps {
  item: AccessControlListItemResponseDto;
  inherited?: boolean;
}

const AccessControlListItem = ({
  item,
  inherited = false,
}: AccessControlListItemProps) => {
  const { t } = useTranslation();
  const { openPopover } = useConfirmPopoverStore();
  const { handleDelete } = selectAccessControlViewModel({});
  return (
    <div className="flex justify-between items-center my-4">
      <div>
        <AccessControlDetail labelKey="role" value={item.role.name} />
        <AccessControlDetail labelKey="company" value={item.object.name} />
        {inherited && item.subject && (
          <AccessControlDetail
            labelKey="inherited_from_user_group"
            value={item.subject.name}
          />
        )}
      </div>
      {!inherited && (
        <IconButton
          icon="trash-2"
          variant="secondary"
          onClick={(e) =>
            openPopover({
              description: t("delete_access_control"),
              onConfirm: () => {
                handleDelete(item.id);
              },
              anchorElement: e.currentTarget,
            })
          }
        />
      )}
    </div>
  );
};

interface AccessControlDetailProps {
  labelKey: string;
  value: string;
}

const AccessControlDetail = ({ labelKey, value }: AccessControlDetailProps) => (
  <div className="flex items-center space-x-1">
    <Typography trans={labelKey} className="font-bold" />
    <span>:</span>
    <Typography>{value}</Typography>
  </div>
);
